import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Search, Download, ChevronLeft, Clock, CheckCircle, XCircle, Send, Eye, RefreshCw, MapPin, RotateCw, FileText } from "lucide-react";
import axios from "axios";
import { SiteSelector, CommercialProvider, useSite } from "./SiteSelector";

const API = '/api';

const EmailCentreContent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedSite } = useSite();
  
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [typeFilter, setTypeFilter] = useState(searchParams.get("type") || "all");
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => { fetchEmails(); }, [page, statusFilter, typeFilter, selectedSite]);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      let url = `${API}/kyle-rise/email-logs?page=${page}&limit=${limit}`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (typeFilter !== "all") url += `&type=${typeFilter}`;
      if (search) url += `&recipient=${encodeURIComponent(search)}`;
      if (selectedSite !== "all") url += `&site_id=${selectedSite}`;
      
      const res = await axios.get(url);
      setEmails(res.data.email_logs || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const exportCSV = () => {
    const headers = ["Email ID", "Type", "Recipient", "Subject", "Status", "Payment Ref", "Created"];
    const rows = emails.map(e => [e.email_id, e.type, e.recipient, e.subject || "", e.status, e.payment_ref || "", new Date(e.created_at).toLocaleString()]);
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `email-logs-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "sent": return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "pending": case "logged": return <Clock className="w-4 h-4 text-yellow-500" />;
      case "failed": return <XCircle className="w-4 h-4 text-red-500" />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="commercial-email-centre">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/commercial")} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
            <div><h1 className="text-xl font-bold text-gray-900">Email Centre</h1><p className="text-sm text-gray-500">{total} emails</p></div>
          </div>
          <div className="flex items-center gap-2">
            <SiteSelector />
            <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"><Download className="w-4 h-4" /> Export</button>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchEmails()} placeholder="Search by recipient..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
          <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="all">All Types</option>
            <option value="receipt">Receipt</option>
            <option value="confirmation">Confirmation</option>
            <option value="reminder">Reminder</option>
            <option value="interest_ack">Interest Ack</option>
          </select>
        </div>
      </div>

      <div className="p-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Recipient</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Related</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Site</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (<tr><td colSpan={8} className="px-4 py-8 text-center"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" /></td></tr>)
              : emails.length === 0 ? (<tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No emails found</td></tr>)
              : emails.map((email) => (
                <tr key={email.email_id} onClick={() => navigate(`/admin/commercial/email-centre/${email.email_id}`)} className="hover:bg-blue-50 cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Mail className="w-5 h-5 text-blue-600" /></div>
                      <div><p className="font-medium text-gray-900 text-sm">{email.email_id}</p>{email.subject && <p className="text-xs text-gray-500 truncate max-w-[200px]">{email.subject}</p>}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">{email.type}</span></td>
                  <td className="px-4 py-3 text-sm text-gray-600">{email.recipient}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">{getStatusIcon(email.status)}<span className={`px-2 py-1 text-xs font-medium rounded-full ${email.status === "sent" ? "bg-green-100 text-green-700" : email.status === "failed" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>{email.status}</span></div>
                  </td>
                  <td className="px-4 py-3">
                    {email.payment_ref ? (<span onClick={(e) => { e.stopPropagation(); navigate(`/admin/commercial/payments/${email.payment_ref}`); }} className="text-sm text-blue-600 hover:underline cursor-pointer">{email.payment_ref.slice(0, 12)}...</span>)
                    : email.interest_ref ? (<span onClick={(e) => { e.stopPropagation(); navigate(`/admin/commercial/interests/${email.interest_ref}`); }} className="text-sm text-blue-600 hover:underline cursor-pointer">{email.interest_ref.slice(0, 12)}...</span>)
                    : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3">{email.site_id ? <span className="flex items-center gap-1 text-xs text-gray-500"><MapPin className="w-3 h-3" /></span> : <span className="text-xs text-gray-400">—</span>}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(email.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1">
                      <button className="p-2 hover:bg-gray-100 rounded-lg" title="View"><Eye className="w-4 h-4 text-gray-500" /></button>
                      {email.status === "failed" && <button className="p-2 hover:bg-gray-100 rounded-lg" title="Retry"><RotateCw className="w-4 h-4 text-blue-500" /></button>}
                      <button className="p-2 hover:bg-gray-100 rounded-lg" title="Template"><FileText className="w-4 h-4 text-purple-500" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (<div className="flex items-center justify-between mt-4"><p className="text-sm text-gray-500">Page {page} of {pages}</p><div className="flex items-center gap-2"><button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Previous</button><button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Next</button></div></div>)}
      </div>
    </div>
  );
};

export default function CommercialEmailCentre() {
  return (
    <CommercialProvider>
      <EmailCentreContent />
    </CommercialProvider>
  );
}
